/**
 * Invoices API Hooks - For payment management
 *
 * Provides type-safe API operations for Invoices with proper error handling,
 * caching, and validation following the established architecture pattern.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query"

import type { InvoiceResponse, InvoiceUpdate } from "@/client/types.gen"
import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import { unwrapStandardResponse } from "@/utils/apiResponse"

import { useApiMutation } from "./useApiMutation"

// Enriched invoice type with household/student info
export interface EnrichedInvoice extends InvoiceResponse {
  household_name?: string
  household_email?: string
  student_name?: string
  student_id?: string
}

export interface InvoiceListResponse {
  items: EnrichedInvoice[]
  total: number
  skip: number
  limit: number
}

export interface InvoiceFilters {
  skip?: number
  limit?: number
  payment_status?: "pending" | "partial" | "paid" | "refunded"
}

/**
 * Fetch paginated list of invoices with enriched data (household/student info)
 */
export function useInvoices(filters: InvoiceFilters = {}) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["invoices", "list", filters],
    queryFn: async (): Promise<InvoiceListResponse> => {
      // Call the list endpoint manually until API client is regenerated
      const response = await api.instance.get("/api/v1/invoices/", {
        params: {
          skip: filters.skip ?? 0,
          limit: filters.limit ?? 100,
          payment_status: filters.payment_status,
        },
      })

      // Axios returns response.data, so unwrap that
      return unwrapStandardResponse<InvoiceListResponse>(
        response.data,
      ) as InvoiceListResponse
    },
    enabled:
      hasPermission("invoices:read") || hasPermission("invoices:read_own"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single invoice by ID with permission checks
 */
export function useInvoice(invoiceId: string) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["invoices", invoiceId],
    queryFn: async (): Promise<InvoiceResponse> => {
      const response = await api.invoices.getInvoiceApiV1InvoicesInvoiceIdGet({
        invoiceId: invoiceId,
      })

      return unwrapStandardResponse<InvoiceResponse>(
        response,
      ) as InvoiceResponse
    },
    enabled:
      !!invoiceId &&
      (hasPermission("invoices:read") || hasPermission("invoices:read_own")),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Update existing invoice (primarily for recording payments)
 *
 * This mutation is used by staff to record payments for enrollments.
 * When a payment is recorded, it updates the invoice's payment fields
 * and automatically recalculates the payment status.
 */
export function useUpdateInvoice() {
  return useApiMutation<
    InvoiceResponse,
    unknown,
    { invoiceId: string; invoiceData: InvoiceUpdate }
  >({
    mutationFn: async ({
      invoiceId,
      invoiceData,
    }: {
      invoiceId: string
      invoiceData: InvoiceUpdate
    }) => {
      // Let the backend handle permission checks
      const response =
        await api.invoices.updateInvoiceApiV1InvoicesInvoiceIdPut({
          invoiceId: invoiceId,
          requestBody: invoiceData,
        })

      return unwrapStandardResponse<InvoiceResponse>(
        response,
      ) as InvoiceResponse
    },
    setQueryData: [
      {
        queryKey: (variables) => ["invoices", variables.invoiceId],
        updater: (_previous, data) => data,
      },
    ],
    // Invalidate both invoices AND enrollments queries since invoice data is nested in enrollment responses
    invalidateQueries: [["invoices"], ["enrollments"]],
    successToast: (data) => ({
      title: "Payment recorded successfully",
      description: `Invoice ${data.id} has been updated.`,
    }),
  })
}

/**
 * Invalidate invoice queries to force a refetch
 */
export function useInvalidateInvoices() {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({ queryKey: ["invoices"] })
    // Also invalidate enrollments since invoice data is nested
    void queryClient.invalidateQueries({ queryKey: ["enrollments"] })
  }
}
